import React, {useState, useEffect, useCallback} from 'react'
import {useTranslation} from 'react-i18next';
import Api from '../../../../api/SMPFetchHelper';
import {TemplateSchema} from '../../../../api/DBSchema/Template';
import SMCTable from '../../../../components/core/SMCTable/SMCTable';
import {ObjectTypes} from '../../../../api/consts';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCButton from '../../../../components/Simple/SMCButton';
import {useBoolean} from '../../../../utils/hooks';
import SetUserModal from '../../SetUserModal';

function extractUserId(userFullName) {
    return userFullName.substring(
        userFullName.lastIndexOf('(') + 1,
        userFullName.lastIndexOf(')')
    );
}

function UsersTab({
    templateObject,
    setTemplateObjectValue
}) {
    const { t } = useTranslation();

    const users = templateObject[TemplateSchema.users];
    const [usersData, setUsersData] = useState([]);
    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(false);

    const [isSetUserModalOpen, openSetUserModal, closeSetUserModal] = useBoolean(false);

    useEffect(() => {
        (async () => {
            if (users) {
                setIsDataLoading(true);
                const usersTableData = [];
                for (let userFullName of users) {
                    const userId = extractUserId(userFullName);
                    const resp = await Api.fetchSingleObject(ObjectTypes.USER, userId);
                    const respData = resp.data.data.user;
                    const newUserData = {
                        fullName: userFullName,
                        email: respData.general.email,
                        role: respData.general.role
                    };
                    usersTableData.push(newUserData);
                }
                setIsDataLoading(false);
                setUsersData(usersTableData);
            }
        })();
    }, [users]);

    const deleteSelected = useCallback(() => {
        if (!users || !users.length || !selectedObjects || !selectedObjects.length) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        const usersWithSelectedRemoved = users.filter(user => !selectedObjects.includes(user));
        setTemplateObjectValue(TemplateSchema.users, usersWithSelectedRemoved);
        setSelectedObjects([]);
    }, [users, selectedObjects, setTemplateObjectValue, t]);

    const handleAdd = useCallback(async (chosenUsers) => {
        if (chosenUsers && chosenUsers.length) {
            const templateUserFullNames = [];
            for (let userId of chosenUsers) {
                const resp = await Api.fetchSingleObject(ObjectTypes.USER, userId);
                const respData = resp.data.data.user;
                // Build user full name in the format of template objects
                templateUserFullNames.push(`${respData.general.firstName} ${respData.general.lastName} (${userId})`);
            }
            const chosenUsersWithoutDuplicates = templateUserFullNames.filter(userFullName => !users.includes(userFullName));
            setTemplateObjectValue(TemplateSchema.users, [...users, ...chosenUsersWithoutDuplicates]);
            closeSetUserModal();
        }
    }, [users, setTemplateObjectValue, closeSetUserModal]);
    
    return (
        <div style={{ width: '100%' }}>
            <SMCLabel>{t('SMC_MESSEGE_SELECT_USERS')}</SMCLabel>
            <RowLayout>
                <SMCButton variant="ghost" icon="new" onClick={openSetUserModal}>{t('SMC_GEN_ADD')}</SMCButton>
                <SMCButton variant="ghost" icon="delete" onClick={deleteSelected}>{t('SMC_GEN_REMOVE')}</SMCButton>
            </RowLayout>
            <SMCTable
                columns={[
                    { id: 'name',  header: t('SMP.NAME'),              width: 200, accessor: 'fullName' },
                    { id: 'email', header: t('SMP.USERS.TABLE.EMAIL'), width: 200, accessor: 'email'    },
                    { id: 'role',  header: t('SMP.USERS.TABLE.ROLE'),  width: 200, accessor: 'role'     }
                ]}
                rowIdSelector={obj => obj.fullName}
                content={usersData}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                height={300}
                loading={isDataLoading}
                hasCheckAll={true}
            />
            <SetUserModal
                isOpen={isSetUserModalOpen}
                onRequestClose={closeSetUserModal}
                onSuccess={handleAdd}
                selectMultiple={true}
                disableSelectById={users.map(extractUserId)}
            />
        </div>
    );
}

export default UsersTab;